import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, Card, CardContent, Grid,
    Autocomplete, Chip, Alert, CircularProgress, Button,
    InputAdornment, Tooltip, styled
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    Search, Restaurant, Inventory, CalendarToday,
    TrendingUp, Warning, CheckCircle, FilterList
} from '@mui/icons-material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase-config';
import appTheme from '../theme';

// **STYLED COMPONENTS WITH LUXURY THEME**
const PageContainer = styled(Box)({
    padding: appTheme.spacing.lg,
    backgroundColor: appTheme.colors.surfaceLuxury,
    minHeight: '100vh',
});

const StyledCard = styled(Card)({
    borderRadius: appTheme.borderRadius.rounded,
    boxShadow: appTheme.shadows.medium,
    border: `1px solid ${appTheme.colors.borderSubtle}`,
    transition: appTheme.transitions.luxury,
    backgroundColor: appTheme.colors.surface,
    "&:hover": {
        boxShadow: appTheme.shadows.strong,
        transform: "translateY(-2px)",
    }
});

const HeaderCard = styled(StyledCard)({
    background: appTheme.gradients.hero,
    color: appTheme.colors.champagne,
    marginBottom: appTheme.spacing.xl,
});

const FilterCard = styled(StyledCard)({
    marginBottom: appTheme.spacing.lg,
    padding: appTheme.spacing.md,
});

const StyledDataGrid = styled(DataGrid)({
    borderRadius: appTheme.borderRadius.rounded,
    border: `1px solid ${appTheme.colors.borderPrimary}`,
    backgroundColor: appTheme.colors.surface,
    '& .MuiDataGrid-columnHeaders': {
        background: appTheme.gradients.hero,
        color: appTheme.colors.champagne,
        fontWeight: appTheme.typography.fontWeights.bold,
        fontSize: appTheme.typography.fontSizes.small,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: 'none',
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
        backgroundColor: appTheme.colors.surfaceElevated,
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: appTheme.colors.jadeFog,
        cursor: 'pointer',
    },
    '& .MuiDataGrid-cell': {
        borderBottom: `1px solid ${appTheme.colors.borderSubtle}`,
    },
});

const ItemTrackingTab = () => {
    // **STATE MANAGEMENT**
    const [restaurants, setRestaurants] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [startDate, setStartDate] = useState(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );
    const [endDate, setEndDate] = useState(new Date());
    const [quantityFilter, setQuantityFilter] = useState('');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [summary, setSummary] = useState({
        totalItems: 0,
        totalOrders: 0,
        totalQuantity: 0,
        avgQuantityPerOrder: 0,
    });

    // **SAFE HELPERS**
    const safeNumber = (value) => {
        if (value === undefined || value === null || isNaN(value)) return 0;
        return Number(value);
    };

    const safeToFixed = (value, decimals = 2) => {
        if (value === undefined || value === null || isNaN(value)) return '0.00';
        return Number(value).toFixed(decimals);
    };

    // **FETCH INITIAL DATA**
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);

                // Fetch restaurants
                const restaurantsSnapshot = await getDocs(collection(db, 'users'));
                const restaurantsList = restaurantsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setRestaurants(restaurantsList);

                // Fetch inventory items
                const itemsSnapshot = await getDocs(collection(db, 'inventoryItems'));
                const itemsList = itemsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setInventoryItems(itemsList);

                setLoading(false);
            } catch (err) {
                console.error('Error fetching initial data:', err);
                setError('Failed to load initial data');
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    // **FETCH TRACKING DATA**
    const fetchTrackingData = useCallback(async () => {
        if (!selectedRestaurant) {
            console.log("⚠️ No restaurant selected");
            setData([]);
            setSummary({ totalItems: 0, totalOrders: 0, totalQuantity: 0, avgQuantityPerOrder: 0 });
            return;
        }

        console.log("🔍 Starting fetchTrackingData for:", selectedRestaurant);
        console.log("📅 Date range:", { startDate, endDate });

        setLoading(true);
        setError('');

        try {
            // **DEBUG: Check if collections exist**
            console.log("🔗 Attempting to connect to Firestore...");

            // **FIXED QUERY: Use proper date handling**
            const invoicesRef = collection(db, 'invoices');

            // Convert dates to proper format
            const startDateForQuery = startDate.toISOString();
            const endDateForQuery = endDate.toISOString();

            console.log("📊 Query parameters:", {
                userId: selectedRestaurant.id,
                startDate: startDateForQuery,
                endDate: endDateForQuery
            });

            // **STEP-BY-STEP QUERY BUILDING**
            console.log("1️⃣ Building query...");

            // Start with basic query
            let q = query(invoicesRef);
            console.log("2️⃣ Base query created");

            // Add userId filter
            q = query(q, where('userId', '==', selectedRestaurant.id));
            console.log("3️⃣ Added userId filter");

            // Add date filters
            q = query(q, where('createdAt', '>=', startDateForQuery));
            console.log("4️⃣ Added start date filter");

            q = query(q, where('createdAt', '<=', endDateForQuery));
            console.log("5️⃣ Added end date filter");

            console.log("📤 Executing query...");
            const invoicesSnapshot = await getDocs(q);
            console.log(`📥 Query successful! Found ${invoicesSnapshot.docs.length} documents`);

            // **DEBUG: Log raw invoice data**
            const invoices = invoicesSnapshot.docs.map(doc => {
                const data = doc.data();
                console.log("📄 Raw invoice data:", {
                    id: doc.id,
                    userId: data.userId,
                    createdAt: data.createdAt,
                    itemsCount: data.items?.length || 0
                });

                // Convert string dates to Date objects
                if (typeof data.createdAt === 'string') {
                    console.log("🔧 Converting string date:", data.createdAt);
                    data.createdAt = new Date(data.createdAt);
                }

                return {
                    id: doc.id,
                    ...data,
                };
            });

            console.log(`📊 Processed ${invoices.length} invoices for aggregation`);

            // **REST OF YOUR EXISTING AGGREGATION CODE**
            const itemStats = {};
            let totalOrdersCount = 0;
            let totalQuantityCount = 0;

            invoices.forEach((invoice, invoiceIndex) => {
                console.log(`📋 Processing invoice ${invoiceIndex + 1}:`, {
                    id: invoice.id,
                    itemsCount: invoice.items?.length,
                    date: invoice.createdAt
                });

                if (!invoice.items || !Array.isArray(invoice.items)) {
                    console.warn(`⚠️ Invoice ${invoice.id} has no items or items is not array`);
                    return;
                }

                totalOrdersCount++;

                invoice.items.forEach((item, itemIndex) => {
                    console.log(`  📦 Item ${itemIndex + 1}:`, {
                        id: item.id,
                        title: item.title,
                        quantity: item.quantity
                    });

                    const key = item.id || item.refId || item.title;

                    if (!itemStats[key]) {
                        itemStats[key] = {
                            itemId: key,
                            itemTitle: item.title,
                            restaurantId: selectedRestaurant.id,
                            restaurantName: selectedRestaurant.restaurantName,
                            totalOrders: 0,
                            totalQuantity: 0,
                            lastOrderedDate: null,
                            averageQuantityPerOrder: 0,
                            lowStockThreshold: 0,
                            monthlyOrderThreshold: 0,
                        };
                    }

                    const quantity = safeNumber(item.quantity);
                    itemStats[key].totalOrders += 1;
                    itemStats[key].totalQuantity += quantity;
                    totalQuantityCount += quantity;

                    // Track last ordered date
                    const orderDate = invoice.createdAt;
                    if (!itemStats[key].lastOrderedDate || orderDate > itemStats[key].lastOrderedDate) {
                        itemStats[key].lastOrderedDate = orderDate;
                    }
                });
            });

            console.log("📈 Aggregation complete:", {
                uniqueItems: Object.keys(itemStats).length,
                totalOrders: totalOrdersCount,
                totalQuantity: totalQuantityCount
            });

            // **FETCH INVENTORY DATA**
            console.log("📦 Fetching inventory items...");
            const inventorySnapshot = await getDocs(collection(db, 'inventoryItems'));
            console.log(`📦 Found ${inventorySnapshot.docs.length} inventory items`);

            const inventoryMap = {};
            inventorySnapshot.docs.forEach(doc => {
                inventoryMap[doc.id] = doc.data();
            });

            // **PROCESS FINAL DATA**
            let processedData = Object.values(itemStats).map(stat => {
                const inventoryItem = inventoryMap[stat.itemId] || {};

                return {
                    ...stat,
                    averageQuantityPerOrder: stat.totalOrders > 0
                        ? safeToFixed(stat.totalQuantity / stat.totalOrders)
                        : '0.00',
                    lastOrderedFormatted: stat.lastOrderedDate
                        ? stat.lastOrderedDate.toLocaleDateString('en-GB')
                        : 'Never',
                    lowStockThreshold: safeNumber(inventoryItem.lowStockThreshold),
                    monthlyOrderThreshold: safeNumber(inventoryItem.monthlyOrderThreshold || inventoryItem.orderingThreshold),
                };
            });

            // Apply filters
            if (selectedItem) {
                console.log("🔍 Applying item filter:", selectedItem.title);
                processedData = processedData.filter(item =>
                    item.itemId === selectedItem.id || item.itemTitle === selectedItem.title
                );
            }

            if (quantityFilter && !isNaN(quantityFilter)) {
                const minQuantity = Number(quantityFilter);
                console.log("🔢 Applying quantity filter:", minQuantity);
                processedData = processedData.filter(item => item.totalQuantity >= minQuantity);
            }

            // Sort by total quantity descending
            processedData.sort((a, b) => b.totalQuantity - a.totalQuantity);

            // Calculate summary statistics
            const summaryStats = {
                totalItems: processedData.length,
                totalOrders: totalOrdersCount,
                totalQuantity: totalQuantityCount,
                avgQuantityPerOrder: totalOrdersCount > 0
                    ? safeToFixed(totalQuantityCount / totalOrdersCount)
                    : '0.00',
            };

            console.log("✅ Final processed data:", {
                itemCount: processedData.length,
                summary: summaryStats
            });

            setData(processedData);
            setSummary(summaryStats);
            setLoading(false);

        } catch (err) {
            console.error('❌ Detailed error in fetchTrackingData:', {
                message: err.message,
                code: err.code,
                details: err.details,
                stack: err.stack
            });

            // **SPECIFIC ERROR HANDLING**
            if (err.code === 'permission-denied') {
                setError('Permission denied. Please check Firestore security rules.');
            } else if (err.code === 'failed-precondition') {
                console.log(err,"ERRORR")
                setError('Database index missing. Check Firestore console for required indexes.',err);
            } else {
                setError(`Failed to fetch tracking data: ${err.message}`);
            }

            setLoading(false);
        }
    }, [selectedRestaurant, selectedItem, startDate, endDate, quantityFilter, inventoryItems]);


    useEffect(() => {
        fetchTrackingData();
    }, [fetchTrackingData]);

    // **DATA GRID COLUMNS**
    const columns = [
        {
            field: 'itemTitle',
            headerName: '📦 Item Name',
            flex: 2,
            minWidth: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Inventory fontSize="small" sx={{ color: appTheme.colors.primary }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'totalOrders',
            headerName: '🔢 Orders',
            flex: 1,
            type: 'number',
            minWidth: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    sx={{
                        backgroundColor: appTheme.colors.primary,
                        color: appTheme.colors.surface,
                        fontWeight: 600,
                    }}
                />
            ),
        },
        {
            field: 'totalQuantity',
            headerName: '📊 Total Qty',
            flex: 1,
            type: 'number',
            minWidth: 120,
            renderCell: (params) => (
                <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: appTheme.colors.success
                }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'averageQuantityPerOrder',
            headerName: '📈 Avg/Order',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: appTheme.colors.secondary }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'lastOrderedFormatted',
            headerName: '📅 Last Ordered',
            flex: 1,
            minWidth: 130,
        },
        {
            field: 'lowStockThreshold',
            headerName: '⚠️ Low Stock Limit',
            flex: 1,
            type: 'number',
            minWidth: 140,
            renderCell: (params) => (
                <Chip
                    label={params.value || 'Not Set'}
                    size="small"
                    sx={{
                        backgroundColor: params.value > 0
                            ? appTheme.colors.warning
                            : appTheme.colors.textMuted,
                        color: appTheme.colors.surface,
                    }}
                />
            ),
        },
        {
            field: 'monthlyOrderThreshold',
            headerName: '📋 Monthly Limit',
            flex: 1,
            type: 'number',
            minWidth: 140,
            renderCell: (params) => (
                <Chip
                    label={params.value || 'Not Set'}
                    size="small"
                    sx={{
                        backgroundColor: params.value > 0
                            ? appTheme.colors.secondary
                            : appTheme.colors.textMuted,
                        color: appTheme.colors.surface,
                    }}
                />
            ),
        },
    ];

    return (
        <PageContainer>
            {/* **HEADER** */}
            <HeaderCard sx={{ padding: appTheme.spacing.xl }}>
                <Typography variant="h3" sx={{
                    fontWeight: appTheme.typography.fontWeights.black,
                    marginBottom: appTheme.spacing.sm,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}>
                    <TrendingUp fontSize="large" />
                    📊 Item Tracking by Restaurant
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Track item usage patterns, frequency, and thresholds across restaurants
                </Typography>
            </HeaderCard>

            {/* **FILTERS SECTION** */}
            <FilterCard>
                <CardContent>
                    <Typography variant="h6" sx={{
                        marginBottom: appTheme.spacing.lg,
                        color: appTheme.colors.primary,
                        fontWeight: appTheme.typography.fontWeights.bold,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}>
                        <FilterList /> Filters & Options
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6} lg={3}>
                            <Autocomplete
                                options={restaurants}
                                getOptionLabel={(option) => option.restaurantName || 'Unknown'}
                                value={selectedRestaurant}
                                onChange={(event, newValue) => setSelectedRestaurant(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Restaurant"
                                        required
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Restaurant sx={{ color: appTheme.colors.primary }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: appTheme.borderRadius.medium,
                                            },
                                        }}
                                    />
                                )}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={3}>
                            <Autocomplete
                                options={inventoryItems}
                                getOptionLabel={(option) => option.title || 'Unknown'}
                                value={selectedItem}
                                onChange={(event, newValue) => setSelectedItem(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Item (Optional)"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Inventory sx={{ color: appTheme.colors.secondary }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: appTheme.borderRadius.medium,
                                            },
                                        }}
                                    />
                                )}
                                size="small"
                                clearOnEscape
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                    label="From Date"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue || startDate)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CalendarToday sx={{ color: appTheme.colors.accent }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: appTheme.borderRadius.medium,
                                                },
                                            }}
                                        />
                                    )}
                                    maxDate={endDate}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={6} lg={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                    label="To Date"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue || endDate)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: appTheme.borderRadius.medium,
                                                },
                                            }}
                                        />
                                    )}
                                    minDate={startDate}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={6} lg={2}>
                            <TextField
                                label="Min Quantity Filter"
                                type="number"
                                value={quantityFilter}
                                onChange={(e) => setQuantityFilter(e.target.value)}
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: appTheme.colors.textSecondary }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: appTheme.borderRadius.medium,
                                    },
                                }}
                                placeholder="e.g. 10"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </FilterCard>

            {/* **ERROR DISPLAY** */}
            {error && (
                <Alert
                    severity="error"
                    sx={{
                        marginBottom: appTheme.spacing.lg,
                        borderRadius: appTheme.borderRadius.medium,
                    }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            {/* **SUMMARY STATISTICS** */}
            {selectedRestaurant && !loading && (
                <Grid container spacing={3} sx={{ marginBottom: appTheme.spacing.xl }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StyledCard>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{
                                    color: appTheme.colors.primary,
                                    fontWeight: appTheme.typography.fontWeights.bold,
                                    marginBottom: appTheme.spacing.sm,
                                }}>
                                    {summary.totalItems}
                                </Typography>
                                <Typography variant="body1" sx={{ color: appTheme.colors.textSecondary }}>
                                    📦 Unique Items
                                </Typography>
                            </CardContent>
                        </StyledCard>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <StyledCard>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{
                                    color: appTheme.colors.secondary,
                                    fontWeight: appTheme.typography.fontWeights.bold,
                                    marginBottom: appTheme.spacing.sm,
                                }}>
                                    {summary.totalOrders}
                                </Typography>
                                <Typography variant="body1" sx={{ color: appTheme.colors.textSecondary }}>
                                    🔢 Total Orders
                                </Typography>
                            </CardContent>
                        </StyledCard>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <StyledCard>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{
                                    color: appTheme.colors.success,
                                    fontWeight: appTheme.typography.fontWeights.bold,
                                    marginBottom: appTheme.spacing.sm,
                                }}>
                                    {summary.totalQuantity}
                                </Typography>
                                <Typography variant="body1" sx={{ color: appTheme.colors.textSecondary }}>
                                    📊 Total Quantity
                                </Typography>
                            </CardContent>
                        </StyledCard>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <StyledCard>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{
                                    color: appTheme.colors.accent,
                                    fontWeight: appTheme.typography.fontWeights.bold,
                                    marginBottom: appTheme.spacing.sm,
                                }}>
                                    {summary.avgQuantityPerOrder}
                                </Typography>
                                <Typography variant="body1" sx={{ color: appTheme.colors.textSecondary }}>
                                    📈 Avg Per Order
                                </Typography>
                            </CardContent>
                        </StyledCard>
                    </Grid>
                </Grid>
            )}

            {/* **DATA GRID** */}
            <StyledCard>
                <CardContent sx={{ padding: 0 }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 400,
                            flexDirection: 'column',
                            gap: 2,
                        }}>
                            <CircularProgress size={50} sx={{ color: appTheme.colors.primary }} />
                            <Typography variant="h6" sx={{ color: appTheme.colors.textSecondary }}>
                                Loading tracking data...
                            </Typography>
                        </Box>
                    ) : !selectedRestaurant ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 400,
                            flexDirection: 'column',
                            gap: 2,
                        }}>
                            <Restaurant sx={{ fontSize: 60, color: appTheme.colors.textMuted }} />
                            <Typography variant="h6" sx={{ color: appTheme.colors.textSecondary }}>
                                Please select a restaurant to view item tracking data
                            </Typography>
                        </Box>
                    ) : data.length === 0 ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 400,
                            flexDirection: 'column',
                            gap: 2,
                        }}>
                            <Inventory sx={{ fontSize: 60, color: appTheme.colors.textMuted }} />
                            <Typography variant="h6" sx={{ color: appTheme.colors.textSecondary }}>
                                No items found for the selected criteria
                            </Typography>
                            <Typography variant="body2" sx={{ color: appTheme.colors.textMuted }}>
                                Try adjusting your date range or quantity filter
                            </Typography>
                        </Box>
                    ) : (
                        <StyledDataGrid
                            rows={data}
                            columns={columns}
                            getRowId={(row) => `${row.itemId}_${row.restaurantId}`}
                            initialState={{
                                pagination: {
                                    paginationModel: { page: 0, pageSize: 25 },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            disableRowSelectionOnClick
                            autoHeight
                            sx={{
                                minHeight: 400,
                                '& .MuiDataGrid-footerContainer': {
                                    backgroundColor: appTheme.colors.surfaceElevated,
                                },
                            }}
                        />
                    )}
                </CardContent>
            </StyledCard>
        </PageContainer>
    );
};

export default ItemTrackingTab;
